<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import ToggleSwitch from 'primevue/toggleswitch'
import http from '@/modules/http.ts'
import BasePage from '@/components/BasePage.vue'
import FloorplanList from '@/components/FloorplanList/index.vue'
import LastMotionList from './components/LastMotionList/index.vue'
import NoMotionList from './components/NoMotionList/index.vue'
import Timer from './components/Timer/index.vue'
import clockIcon from '@/assets/clock.svg'
import store, { LockdownEvent } from './store'
import { Floorplan, ImageProcessingStatus } from '@/entities/Floorplan'
import { removeMap, createMap, redrawMap, startFeatureAnimation } from './editor'
import locationStore from '@/stores/location'
import { createLogger } from '@/utils/logger'
import { fakeInitialEvents, simulateRandomEvents } from './utils/generateEvents'
import { animate, clearLockdownStoreData } from './utils/animate'
import {
  startMessageReceiver,
  changeMessageReceiverChannel,
  stopMessageReceiver,
  isMessageReceiverInitialized,
} from '@/modules/messageReceiver.js'

const logger = createLogger('layer-lockdown-mode:index')

defineOptions({
  name: 'LayerLockdownMode',
})
const toast = useToast()
const route = useRoute()
const router = useRouter()
const isPageLoading = ref(true)
const isFloorplanLoading = ref(false)

const leafletElement = ref(null)
let stopEventGeneration = () => {}
const demoMode = ref(false)

watch(
  () => route.params.floorplanId,
  async (newId) => {
    isFloorplanLoading.value = true

    await fetchInitialData()

    isFloorplanLoading.value = false
    if (demoMode.value) {
      demoMode.value = false
    }

    await openFloorplan(newId as string)

    // Re-trigger animations for current events if incident is ongoing
    if (store.incident?.ongoing) {
      store.incident.events.forEach((event) => {
        startFeatureAnimation(event)
      })
    } else {
      await fetchIncidentData()
    }
  },
)

watch(
  () => store.featuresToAnimate.size,
  () => {
    requestAnimationFrame(() => animate())
  },
)

watch(
  () => demoMode.value,
  (demoMode) => {
    if (demoMode) {
      const groups = store.activeFloorplan?.spaces?.flatMap((space) => space.groups || []) || []
      fakeInitialEvents(groups)
      stopEventGeneration = simulateRandomEvents(groups)
      store.setAnimationRunning(true)
      requestAnimationFrame(() => animate())
    } else {
      stopEventGeneration()
      clearLockdownStoreData()
      redrawMap(store.features)
    }
  },
)

watch(
  () => store.incident?.ongoing,
  (ongoingIncident) => {
    if (ongoingIncident) {
      store.incident.events.forEach((event) => {
        startFeatureAnimation(event)
      })
      store.setAnimationRunning(true)
    } else {
      clearLockdownStoreData()
      redrawMap(store.features)
    }
  },
)

onMounted(async () => {
  const { floorplanId } = route.params

  if (!floorplanId) {
    goToFloorplan(store.floorplans[0])
  }

  await fetchInitialData()

  if (!store.hasFloorplans()) {
    logger.debug(`Location has no floorplans yet`)
    isPageLoading.value = false
    return
  }

  isPageLoading.value = false
  await openFloorplan(floorplanId as string)
  await fetchIncidentData()
})

onBeforeUnmount(() => {
  stopEventGeneration?.()
  clearLockdownStoreData()
  stopMessageReceiver()
})

async function fetchInitialData() {
  const { floorplans, activeFloorplan } = await http.get<{ floorplans: Floorplan[]; activeFloorplan: Floorplan }>(
    `/locations/${route.params.locationId}/layers/lockdown-mode/floorplans/${route.params.floorplanId}/fetchInitialData`,
  )

  store.setFloorplans(floorplans)
  store.setActiveFloorplan(activeFloorplan)
  logger.debug(`State after fetchInitialData:`, store)
}

async function fetchIncidentData() {
  try {
    const incidentData = await http.get<{ events: LockdownEvent[]; incidentStartTime: string; incidentId: string }>(
      `/locations/${route.params.locationId}/layers/lockdown-mode/${route.params.floorplanId}/fetchIncidentData`,
    )
    if (incidentData) {
      store.setIncident({
        ongoing: true,
        startTime: incidentData.incidentStartTime,
        id: incidentData.incidentId,
        events: incidentData.events,
      })
    }
  } catch (e) {
    const message = e.response?.data
    if (message == 'No ongoing incident found') {
      store.setIncident({
        ongoing: false,
        startTime: '',
        id: '',
        events: [],
      })
    } else {
      logger.error('Error fetching incident data', e)
    }
  }
}

async function openFloorplan(floorplanId: string) {
  if (floorplanId) {
    await connectMessageReceiver()
    removeMap()
    nextTick(() => {
      createMap(leafletElement)
      redrawMap(store.features)
    })
  }
}

function isImageAvailable() {
  return store.activeFloorplan?.image_processing_status === ImageProcessingStatus.Ready
}

function goToFloorplan(floorplan: Floorplan) {
  router.push({
    name: 'layers-lockdown-mode',
    params: { locationId: route.params.locationId, floorplanId: floorplan.uuid },
  })
}

async function connectMessageReceiver() {
  try {
    if (isMessageReceiverInitialized()) {
      changeMessageReceiverChannel(getMessageChannelName(), () => {}, messageHandler)
    } else {
      const pubsubConfig: any = await http.post(`/pubsub/config`)
      startMessageReceiver(pubsubConfig, getMessageChannelName(), () => {}, messageHandler)
    }
  } catch (e) {
    logger.error('Error connecting to notification service', e)
    toast.add({
      summary: `Error connecting to notification service`,
      detail: `${e.message}`,
      life: 1300,
      severity: 'error',
    })
  }
}

function getMessageChannelName() {
  return `${route.params.locationId}-${route.params.floorplanId}-lockdown`
}

function messageHandler(_eventName: string, data: any) {
  if (_eventName === 'dvp:lockdown:update') {
    switch (data.type) {
      case 'lockdown_incident':
        const ongoing = data.lockdown_mode
        if (!ongoing) {
          clearLockdownStoreData()
        }
        store.setIncident({
          ongoing,
          startTime: data.started_at,
          id: data.uuid,
          events: [],
        })
        break
      case 'motion_event':
        const event = {
          type: data.feature_type,
          pid: data.feature_id,
          lastMotionAt: new Date(data.event_at).toISOString(),
        }
        store.updateIncidentEvents(event)
        startFeatureAnimation(event)
        break
      default:
        break
    }
  }
}
</script>

<template>
  <BasePage title="Lockdown Layer" :location="locationStore.location">
    <template #sidebar>
      <section class="sidebar-content">
        <Timer :timer-on="demoMode || store.incident?.ongoing" :time="store.incident?.startTime" />
        <FloorplanList
          :loading="isPageLoading"
          :floorplans="store.floorplans"
          :activeFloorplan="store.activeFloorplan"
          @floorplanSelected="goToFloorplan"
        />
        <div class="demo-mode-container">
          <div class="top-section">
            <LastMotionList />
            <NoMotionList />
          </div>
          <div class="bottom-section" v-if="!store.incident?.ongoing">
            <p class="demo-mode-disclaimer">Demo Mode will not trigger real devices.</p>
            <div class="demo-mode-toggle">
              <ToggleSwitch :modelValue="demoMode" @update:modelValue="(val) => (demoMode = val)" />
              Demo Mode
            </div>
          </div>
        </div>
      </section>
    </template>
    <template #main>
      <div class="main-content">
        <div class="main-right-content">
          <div v-if="isPageLoading || isFloorplanLoading" class="main-loading">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
          <div v-else-if="!isImageAvailable()" class="converting-floorplan-image">
            <img :src="clockIcon" width="100" alt="clock" />
            Your floor plan is being created. It will be ready soon.
          </div>
          <div
            v-show="(!isPageLoading || !isFloorplanLoading) && store.floorplans.length && isImageAvailable()"
            ref="leafletElement"
            class="leaflet"
          ></div>
        </div>
      </div>
    </template>
  </BasePage>
</template>

<style scoped>
section.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: var(--section-padding);
  height: 100%;
  justify-content: space-between;
}

.search-input {
  width: 100%;
}

section.sidebar-content .spaces-list {
  overflow-y: auto;
  overflow-x: visible;
  margin: 2px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
}

section.footer {
  flex: 0.1;
  max-height: 35px;
}

.save-button {
  width: 100%;
  display: flex;
  justify-content: center;
}
.leaflet {
  height: 100%;
  background-color: #fff;
}
.main-loading {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--p-surface-300);
}
.main-loading i {
  font-size: 32px;
}
.no-floorplans {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
}
.converting-floorplan-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
}

.space-card {
  cursor: pointer;
}

.main-content {
  display: flex;
  height: 100%;
  width: 100%;
}

.main-right-content {
  flex-grow: 1;
  transition: flex-grow 0.3s ease;
}

.main-content-enter-active .main-right-content,
.main-content-leave-active .main-right-content {
  transition: flex-grow 0.3s ease;
}

.main-content-enter .main-right-content,
.main-content-leave-to .main-right-content {
  flex-grow: 0;
}
.demo-mode-container {
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  height: 100%;
}

.bottom-section {
  margin-top: auto;
}
.demo-mode-disclaimer {
  align-self: flex-end;
  font-size: 14px;
  font-weight: 400;
  color: var(--grayscale-800, #49494a);
}
.demo-mode-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 1rem;
}
</style>
